import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import FoodScanner from '../components/FoodScanner';
import FoodDatabase from '../components/FoodDatabase';
import SignInRequired from '../components/SignInRequired';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = () => {
  const [profile, setProfile] = useState(null);
  const [todayData, setTodayData] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [showFoodDB, setShowFoodDB] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, todayRes] = await Promise.all([
        axios.get(`${API}/profile`),
        axios.get(`${API}/meals/today`)
      ]);
      setProfile(profileRes.data);
      setTodayData(todayRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 404) {
        navigate('/profile-setup');
      }
    } finally {
      setLoading(false);
    }
  };

  const deleteMeal = async (mealId) => {
    if (!window.confirm('Are you sure you want to delete this meal?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/meals/${mealId}`);
      fetchData(); // Refresh the data
    } catch (error) {
      alert('Failed to delete meal: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(243, 234, 255, 0.8) 0%, rgba(255, 232, 240, 0.8) 25%, rgba(232, 244, 255, 0.8) 50%, rgba(255, 232, 213, 0.8) 75%, rgba(243, 234, 255, 0.8) 100%)' }}>
        <p className="body-large">Loading...</p>
      </div>
    );
  }

  const caloriesConsumed = todayData?.totals?.calories || 0;
  const calorieGoal = profile?.calorie_goal || 2000;
  const caloriesRemaining = calorieGoal - caloriesConsumed;
  const progressPercent = Math.min((caloriesConsumed / calorieGoal) * 100, 100);

  return (
    <SignInRequired featureName="Meal Tracker">
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-8">
        {/* Daily Summary Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="heading-3 mb-4" style={{ color: 'rgb(24, 24, 24)' }}>Today's Progress</h2>
          
          <div className="mb-6">
            <div className="flex justify-between mb-2">
              <span className="body-medium" style={{ color: 'rgb(24, 24, 24)' }}>Calories</span>
              <span className="body-medium" style={{ color: 'rgb(112, 112, 112)' }}>
                {Math.round(caloriesConsumed)} / {Math.round(calorieGoal)}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="h-3 rounded-full transition-all duration-300"
                style={{ 
                  width: `${progressPercent}%`,
                  background: progressPercent > 100 ? '#ef4444' : 'linear-gradient(168deg, rgb(67, 67, 67) -63%, rgb(0, 0, 0) 100%)'
                }}
              />
            </div>
            <p className="body-small mt-2" style={{ color: caloriesRemaining >= 0 ? 'rgb(16, 185, 129)' : 'rgb(239, 68, 68)' }}>
              {caloriesRemaining >= 0 ? `${Math.round(caloriesRemaining)} calories remaining` : `${Math.abs(Math.round(caloriesRemaining))} calories over limit`}
            </p>
          </div>

          {/* Macros */}
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 rounded-lg" style={{ backgroundColor: 'rgba(243, 234, 255, 0.3)' }}>
              <p className="body-small mb-1" style={{ color: 'rgb(112, 112, 112)' }}>Protein</p>
              <p className="heading-4" style={{ color: 'rgb(24, 24, 24)' }}>{Math.round(todayData?.totals?.protein || 0)}g</p>
              <p className="caption" style={{ color: 'rgb(143, 143, 143)' }}>of {Math.round(profile?.protein_goal || 0)}g</p>
            </div>
            <div className="text-center p-4 rounded-lg" style={{ backgroundColor: 'rgba(243, 234, 255, 0.3)' }}>
              <p className="body-small mb-1" style={{ color: 'rgb(112, 112, 112)' }}>Carbs</p>
              <p className="heading-4" style={{ color: 'rgb(24, 24, 24)' }}>{Math.round(todayData?.totals?.carbs || 0)}g</p>
              <p className="caption" style={{ color: 'rgb(143, 143, 143)' }}>of {Math.round(profile?.carbs_goal || 0)}g</p>
            </div>
            <div className="text-center p-4 rounded-lg" style={{ backgroundColor: 'rgba(243, 234, 255, 0.3)' }}>
              <p className="body-small mb-1" style={{ color: 'rgb(112, 112, 112)' }}>Fat</p>
              <p className="heading-4" style={{ color: 'rgb(24, 24, 24)' }}>{Math.round(todayData?.totals?.fat || 0)}g</p>
              <p className="caption" style={{ color: 'rgb(143, 143, 143)' }}>of {Math.round(profile?.fat_goal || 0)}g</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <button onClick={() => setShowScanner(true)} className="btn-primary p-6 text-lg">
            📸 Scan Food
          </button>
          <button onClick={() => setShowFoodDB(true)} className="btn-secondary p-6 text-lg">
            🗂️ Food Database
          </button>
          <button onClick={() => navigate('/meal-planner')} className="btn-secondary p-6 text-lg">
            🤖 Plan Meals
          </button>
        </div>

        {/* Today's Meals */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h2 className="heading-3 mb-4" style={{ color: 'rgb(24, 24, 24)' }}>Today's Meals</h2>
          {todayData?.meals?.length === 0 ? (
            <p className="body-medium text-center py-8" style={{ color: 'rgb(112, 112, 112)' }}>No meals logged yet. Start by scanning your food!</p>
          ) : (
            <div className="space-y-3">
              {todayData?.meals?.map((meal) => (
                <div key={meal.id} className="p-4 rounded-lg border hover:shadow-sm transition-shadow" style={{ borderColor: 'rgba(24, 24, 24, 0.1)' }}>
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <p className="body-medium font-semibold" style={{ color: 'rgb(24, 24, 24)' }}>{meal.food_name}</p>
                      <p className="body-small" style={{ color: 'rgb(112, 112, 112)' }}>
                        {new Date(meal.timestamp).toLocaleTimeString()} • {meal.meal_type}
                      </p>
                    </div>
                    <div className="text-right flex-1">
                      <p className="body-medium font-semibold" style={{ color: 'rgb(24, 24, 24)' }}>{Math.round(meal.calories)} cal</p>
                      <p className="body-small" style={{ color: 'rgb(112, 112, 112)' }}>
                        P: {Math.round(meal.protein)}g • C: {Math.round(meal.carbs)}g • F: {Math.round(meal.fat)}g
                      </p>
                    </div>
                    <button
                      onClick={() => deleteMeal(meal.id)}
                      className="p-2 rounded-lg hover:bg-red-50 transition-colors"
                      title="Delete meal"
                      style={{ color: 'rgb(239, 68, 68)' }}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Food Scanner Modal */}
      {showScanner && <FoodScanner onClose={() => { setShowScanner(false); fetchData(); }} />}
      
      {/* Food Database Modal */}
      {showFoodDB && <FoodDatabase onClose={() => { setShowFoodDB(false); fetchData(); }} />}
    </div>
    </SignInRequired>
  );
};

export default DashboardPage;
